/**
 * Typed protocol client over any Transport: builds envelopes, maps error responses to BackendError, keeps
 * the auth session and restores it after WebSocket reconnects, and dispatches typed server events.
 *
 * Auth session (bearer credentials only, no cookies): the sign-in result's access token authorizes requests (HTTP:
 * `Authorization: Bearer`; WebSocket / in-process: the connection is bound). The refresh token and the guest device
 * token are kept in the CredentialStore (one entry per backend origin). An access token about to expire is refreshed
 * by a timer; a request refused with UNAUTHORIZED 'token_expired' is refreshed (one refresh at a time) and sent again
 * under the same request id; a reconnected WebSocket is bound again with the access token (a fresh one when it
 * expired).
 *
 * Accounts: `signUp` / `signIn` (nickname + password: the password goes into that one request and is kept nowhere),
 * `restoreSession` (an app start: the stored credentials of this origin -> auth.resume / auth.refresh; a refused one is
 * wiped and its reason reported), `signOut` (auth.logout, then this origin's stored credentials are wiped). Guest
 * accounts: `ensureSession` falls back to the guest sign-in with the device token only when the refresh token is gone
 * or refused, and only when the caller allows it (`{ guest: true }`, the default of this protocol-level client; the
 * game's facade decides explicitly, src/client/game-services.ts).
 *
 * One active session per account (the server's default): a sign-in of the account somewhere else ends this client's
 * session (WebSocket close code 4004, UNAUTHORIZED 'session_replaced'). The client then drops its session and its
 * refresh token, does not reconnect by itself, reports it once to the `onSessionReplaced` listeners (`sessionReplaced`
 * stays true) and never signs in again on its own: `ensureSession` refuses with UNAUTHORIZED 'session_replaced' until
 * the player asks to sign in here again (`ensureSession(.., { takeOver: true })`, or an explicit sign-in), which in turn
 * ends the other session. A resume of this client's own session (back from the background) replaces nothing.
 */
import { cryptoRandomSource, randomId } from '../../shared/random';
import type { EventEnvelope, RequestEnvelope, ResponseEnvelope } from '../../shared/protocol/envelope';
import { BackendError } from '../../shared/protocol/errors';
import type { EventBatch, EventMap, EventType, HelloPayload, HelloResult, PayloadOf, RequestType, ResultOf } from '../../shared/protocol/messages';
import { type AuthFailureReason, type AuthResult, type DeviceInfo, type MessageLanguage, type SignedInResult, WS_CLOSE_CODES } from '../../shared/protocol/model';
import { APP_VERSION, PROTOCOL_VERSION, SUPPORTED_PROTOCOL_VERSIONS } from '../../shared/protocol/version';
import type { CredentialStore } from './credentials';
import { DEFAULT_REQUEST_TIMEOUT_MS, type Transport, TransportError } from './transport';

export interface BackendClientOptions {
  transport: Transport;
  /** The app's version for system.hello (default DEFAULT_CLIENT_VERSION). */
  clientVersion?: string;
  /** system.hello platform (default: `device.platform`, else 'web' in a browser page, 'node' elsewhere). */
  platform?: string;
  /** system.hello capabilities (default CLIENT_CAPABILITIES). */
  capabilities?: string[];
  credentials?: CredentialStore;
  /** Retries (same request id) for transport failures and retryable error codes. Default DEFAULT_CLIENT_RETRIES (1). */
  retries?: number;
  timeoutMs?: number;
  /** This installation, sent with every auth request (the server shows it in `auth.sessions`). */
  device?: DeviceInfo;
  /** Refresh the access token by a timer shortly before it expires (default true). */
  autoRefresh?: boolean;
}

export interface ClientSession {
  playerId: string;
  displayName: string;
  /** Current access token. */
  token: string;
  /** Server-time expiry of the access token. */
  expiresAt: number;
  username: string | null;
  /** Device session (auth.sessions / auth.revoke); null for a backend without device sessions. */
  sessionId: string | null;
}

/** Requests that sign in: their result becomes the session; they are never refreshed and repeated. */
const SIGN_IN_TYPES: ReadonlySet<RequestType> = new Set([
  'auth.guest',
  'auth.signUp',
  'auth.signIn',
  'auth.playGames',
  'auth.emailSignUp',
  'auth.emailSignIn',
  'auth.register',
  'auth.login',
  'auth.resume',
  'auth.refresh',
]);
/** Requests whose result is adopted as the session (auth.link rotates the session's tokens). */
const AUTH_TYPES: ReadonlySet<RequestType> = new Set([...SIGN_IN_TYPES, 'auth.link']);

/** `details.reason` of an UNAUTHORIZED error (null when there is none). */
export function authFailureReason(err: { code?: string; details?: unknown } | null | undefined): AuthFailureReason | null {
  if (!err || err.code !== 'UNAUTHORIZED') return null;
  const reason = (err.details as { reason?: unknown } | undefined)?.reason;
  return typeof reason === 'string' ? (reason as AuthFailureReason) : null;
}

/**
 * Why `restoreSession` found no session: 'none' = nothing stored for this origin; 'expired' = the stored session ran
 * out (refresh token expired); 'revoked' = it was ended (sign-out elsewhere, auth.revoke, the session limit, a detected
 * refresh token reuse); 'replaced' = the account signed in on another device, which ended this one's session; 'invalid' =
 * the server does not know the stored tokens (another server, a reset data directory).
 */
export type RestoreFailureReason = 'none' | 'expired' | 'revoked' | 'replaced' | 'invalid';

export type RestoreOutcome = { restored: true } | { restored: false; reason: RestoreFailureReason };

/** The restore failure a refused stored credential means. */
export function restoreFailureOf(reason: AuthFailureReason | null): RestoreFailureReason {
  if (reason === 'refresh_expired' || reason === 'token_expired') return 'expired';
  if (reason === 'revoked' || reason === 'refresh_reused') return 'revoked';
  if (reason === 'session_replaced') return 'replaced';
  return 'invalid';
}

/** Local-clock margin before its expiry from which an access token counts as expired (at most 10% of its lifetime). */
const ACCESS_EXPIRY_MARGIN_MS = 5_000;
/** How long BackendClient.wake waits for the answer of its probe before it replaces a connection that looks open. */
export const WAKE_PROBE_TIMEOUT_MS = 5_000;

/** Automatic retries of a request by default (BackendClientOptions.retries). */
export const DEFAULT_CLIENT_RETRIES = 1;

/** Version this web client reports in system.hello (the app version of package.json). */
export const DEFAULT_CLIENT_VERSION = `fwp-web/${APP_VERSION}`;

/** What this client library supports (system.hello capabilities). */
export const CLIENT_CAPABILITIES: readonly string[] = ['events.resume', 'events.gap', 'pagination'];

/**
 * Events after `after` could not be delivered (they fell out of the server's replay buffer, the server restarted, or
 * the position was unknown): the app must reload its state (profile, rooms, sessions). Events continue after `last`.
 */
export interface EventGap {
  after: number | null;
  last: number;
}

function defaultPlatform(): string {
  return typeof (globalThis as { document?: unknown }).document !== 'undefined' ? 'web' : 'node';
}

/** Pause before automatic retry number `attempt` (1-based). */
const retryPauseMs = (attempt: number) => 250 * attempt;

/**
 * Longest time one `call` takes to fail without a reply: every attempt waits the full request timeout, plus the pauses
 * between the attempts. The game's bridge watchdog (src/game/runtime/net-policy.ts netCallTimeoutMs) waits longer.
 */
export function requestBudgetMs(timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS, retries = DEFAULT_CLIENT_RETRIES): number {
  let ms = (retries + 1) * timeoutMs;
  for (let attempt = 1; attempt <= retries; attempt++) ms += retryPauseMs(attempt);
  return ms;
}

/**
 * UNAVAILABLE without a reply of the backend (a transport failure or a timeout): the request may or may not have been
 * applied. An error reply of the backend (even UNAVAILABLE) is a plain BackendError: the backend answered it.
 */
export class NoReplyError extends BackendError {
  constructor(message: string) {
    super('UNAVAILABLE', message);
    this.name = 'NoReplyError';
  }
}

export class BackendClient {
  readonly transport: Transport;
  private session: ClientSession | null = null;
  private readonly idPrefix = randomId(cryptoRandomSource(), '', 6);
  private seq = 0;
  private readonly handlers = new Map<string, Set<(payload: unknown) => void>>();
  private readonly anyHandlers = new Set<(ev: EventEnvelope) => void>();
  private readonly offTransport: (() => void)[] = [];
  /** Seq of the newest event of `eventsPlayer` this client has (null = none known yet). */
  private lastEventSeq: number | null = null;
  private eventsPlayer: string | null = null;
  /** Live events held back while the missed ones are resumed after a reconnect. */
  private heldEvents: EventEnvelope[] | null = null;
  private readonly gapListeners = new Set<(gap: EventGap) => void>();
  /** A reconnect happened and its missed events were not resumed (its sign-in failed): a gap at the next sign-in. */
  private resumePending = false;
  /** The server's system.hello answer (features, limits, versions); null before hello(). */
  serverInfo: HelloResult | null = null;
  /** Refresh token of the device session `refreshSessionId` (also in the CredentialStore). */
  private refreshToken: string | null = null;
  private refreshSessionId: string | null = null;
  private refreshExpiresAt: number | null = null;
  /** The refresh in flight (one at a time). */
  private refreshing: Promise<boolean> | null = null;
  /**
   * Request id of the auth.refresh of a refresh token: every attempt with the same token goes under the same id (a
   * refresh whose reply was lost is then answered with a valid pair, not treated as a reuse of a rotated token).
   */
  private refreshRequest: { token: string; id: string } | null = null;
  /** Local-clock time from which the access token counts as expired, its expiry minus a margin (0 = unknown). */
  private accessDeadline = 0;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private closed = false;
  /** The session was replaced by a sign-in elsewhere (see the module comment); cleared by the next sign-in. */
  private replaced = false;
  private readonly replacedListeners = new Set<() => void>();

  constructor(private readonly opts: BackendClientOptions) {
    this.transport = opts.transport;
    this.offTransport.push(this.transport.onEvent((ev) => this.dispatch(ev)));
    // HTTP long polling resumes by itself (events.poll after the last seq); it reports a gap here.
    const offGap = this.transport.onGap?.((gap) => {
      this.lastEventSeq = gap.last;
      this.emitGap(gap);
    });
    if (offGap) this.offTransport.push(offGap);
    const offClose = this.transport.onServerClose?.((close) => {
      if (close.code === WS_CLOSE_CODES.sessionReplaced) this.markReplaced();
    });
    if (offClose) this.offTransport.push(offClose);
    // Re-authenticate a re-established connection before any queued request is released: bind it with the access
    // token, or with a fresh pair when the token expired.
    this.transport.setReconnectHandler?.(async (send) => {
      // Events of the outage are resumed below, or after the next sign-in when this one does not succeed.
      this.resumePending = this.eventsPlayer !== null;
      const s = this.session;
      if (!s) return;
      // Events pushed on the new connection before the missed ones are resumed (e.g. the room.updated this very
      // sign-in causes) are held: delivered first, their seq would move the position past the missed events.
      this.heldEvents ??= [];
      let resuming = false;
      const envelope = (type: RequestType, payload: object): RequestEnvelope => ({ v: PROTOCOL_VERSION, id: this.nextId(), type, payload });
      try {
        const refreshToken = this.currentRefreshToken();
        const expiring = this.accessDeadline !== 0 && Date.now() >= this.accessDeadline;
        let res = !expiring || !refreshToken ? await send(envelope('auth.resume', this.withDevice({ token: s.token }))) : null;
        if (refreshToken && (res === null || (!res.ok && authFailureReason(res.error) === 'token_expired'))) {
          res = await send({ ...envelope('auth.refresh', this.withDevice({ refreshToken })), id: this.refreshRequestId(refreshToken) });
          if (!res.ok && res.error.code === 'UNAUTHORIZED') this.forgetRefreshToken(refreshToken);
        }
        if (!res) return;
        if (res.ok) {
          this.adoptSession(res.result as AuthResult);
          resuming = true;
          await this.resumeEvents(send);
        }
        else if (authFailureReason(res.error) === 'session_replaced') this.markReplaced();
        else if (res.error.code === 'UNAUTHORIZED' || res.error.code === 'BANNED') this.dropSession(false, authFailureReason(res.error) === 'revoked');
      } catch {
        /* the connection dropped again; the next reconnect retries */
      } finally {
        // Not resumed (the sign-in failed): the held events go out as they are (the next sign-in reports the gap).
        if (!resuming) this.releaseHeld();
      }
    });
  }

  get currentSession(): ClientSession | null {
    return this.session;
  }

  /** True after the session was replaced by a sign-in of the account elsewhere, until the next sign-in here. */
  get sessionReplaced(): boolean {
    return this.replaced;
  }

  /** Called once each time the session is replaced by a sign-in elsewhere (see the module comment). */
  onSessionReplaced(listener: () => void): () => void {
    this.replacedListeners.add(listener);
    return () => this.replacedListeners.delete(listener);
  }

  /**
   * The server ended this session because the account signed in somewhere else: the session and its refresh token are
   * dropped (the stored player stays for the sign-in form), and the listeners hear it once.
   */
  private markReplaced(): void {
    const had = this.session !== null || this.refreshToken !== null;
    this.dropSession(false, true);
    this.refreshRequest = null;
    if (this.replaced && !had) return;
    this.replaced = true;
    for (const l of [...this.replacedListeners]) {
      try {
        l();
      } catch {
        /* listener errors are isolated */
      }
    }
  }

  get playerId(): string | null {
    return this.session?.playerId ?? null;
  }

  private nextId(): string {
    this.seq += 1;
    return `${this.idPrefix}-${this.seq.toString(36)}`;
  }

  /** A fresh request id (for callers that resend a mutation with the same id, see `call` opts.id). */
  newRequestId(): string {
    return this.nextId();
  }

  /**
   * Sends a typed request. Throws BackendError for error responses and NoReplyError (UNAVAILABLE) for transport failures.
   * `opts.id` resends a request under an earlier id: the backend answers a repeated mutation from its idempotency cache.
   */
  async call<T extends RequestType>(
    type: T,
    payload: PayloadOf<T>,
    opts: { timeoutMs?: number; retries?: number; id?: string; correlationId?: string } = {},
  ): Promise<ResultOf<T>> {
    const request: RequestEnvelope<T, PayloadOf<T>> = { v: PROTOCOL_VERSION, id: opts.id ?? this.nextId(), type, payload };
    if (opts.correlationId !== undefined) request.correlationId = opts.correlationId;
    const retries = opts.retries ?? this.opts.retries ?? DEFAULT_CLIENT_RETRIES;
    let attempt = 0;
    let refreshed = false;
    // An access token known to be expired (e.g. after the device slept) is refreshed before the request.
    if (!SIGN_IN_TYPES.has(type) && this.session && this.accessDeadline !== 0 && Date.now() >= this.accessDeadline) {
      await this.refreshSession().catch(() => false);
    }
    for (;;) {
      const sentWith = this.session?.token ?? null;
      let response;
      try {
        response = await this.transport.send(request, { timeoutMs: opts.timeoutMs ?? this.opts.timeoutMs });
      } catch (err) {
        if (err instanceof TransportError && err.retryable && attempt < retries) {
          attempt++;
          await new Promise((r) => setTimeout(r, retryPauseMs(attempt)));
          continue;
        }
        throw new NoReplyError(err instanceof Error ? err.message : String(err));
      }
      if (response.ok) {
        const result = response.result as ResultOf<T>;
        if (AUTH_TYPES.has(type)) this.adoptSession(result as unknown as AuthResult);
        if (type === 'auth.logout') this.dropSession();
        if (type === 'auth.revoke' && this.session && (payload as { sessionId?: string }).sessionId === this.session.sessionId) this.dropSession();
        return result;
      }
      if (response.error.code === 'UNAVAILABLE' && attempt < retries) {
        attempt++;
        await new Promise((r) => setTimeout(r, retryPauseMs(attempt)));
        continue;
      }
      // A wrong password confirming account.delete or auth.emailChange is no failure of the session.
      const passwordRefused = (type === 'account.delete' || type === 'auth.emailChange') && authFailureReason(response.error) === 'invalid_credentials';
      if (response.error.code === 'UNAUTHORIZED' && !SIGN_IN_TYPES.has(type) && this.session && !passwordRefused) {
        const reason = authFailureReason(response.error);
        // The refused request was not executed: after a refresh it is sent again under the same request id.
        if (reason === 'token_expired' && !refreshed) {
          refreshed = true;
          // A concurrent request already refreshed the session: send it again with the new token.
          if (this.session.token !== sentWith) continue;
          if (await this.refreshSession()) continue;
        }
        if (reason === 'session_replaced') this.markReplaced();
        else this.dropSession(false, reason === 'revoked');
      }
      if (SIGN_IN_TYPES.has(type) && (type === 'auth.resume' || type === 'auth.refresh') && authFailureReason(response.error) === 'session_replaced') this.markReplaced();
      throw BackendError.fromProtocol(response.error);
    }
  }

  /**
   * auth.signUp: a new nickname account, signed in at once. The password is part of this one request only (a retry of
   * a lost reply resends the same envelope); nothing keeps it afterwards.
   */
  signUp(nickname: string, password: string, opts: { id?: string } = {}): Promise<SignedInResult> {
    return this.call('auth.signUp', this.withDevice({ nickname, password }), opts);
  }

  /** auth.signIn with a nickname and a password (see signUp for the password). */
  signIn(nickname: string, password: string, opts: { id?: string } = {}): Promise<SignedInResult> {
    return this.call('auth.signIn', this.withDevice({ nickname, password }), opts);
  }

  /**
   * auth.emailSignUp: a new nickname account with an e-mail address (pending until its link is opened), signed in at
   * once; auth.emailSignIn: sign-in with a confirmed address. See signUp for the password.
   */
  signUpWithEmail(nickname: string, email: string, password: string, lang?: MessageLanguage, opts: { id?: string } = {}): Promise<SignedInResult> {
    return this.call('auth.emailSignUp', this.withDevice({ nickname, email, password, ...(lang ? { lang } : {}) }), opts);
  }

  signInWithEmail(email: string, password: string, opts: { id?: string } = {}): Promise<SignedInResult> {
    return this.call('auth.emailSignIn', this.withDevice({ email, password }), opts);
  }

  /** auth.playGames: the account linked to the Play Games player of a server auth code (single-use, never stored). */
  signInWithPlayGames(serverAuthCode: string): Promise<SignedInResult> {
    return this.call('auth.playGames', this.withDevice({ serverAuthCode }));
  }

  /**
   * Restores the session stored for this backend origin (an app start): the stored access token -> auth.resume, else
   * (or when it expired) the stored refresh token -> auth.refresh. Never signs in as a guest. A refused credential is
   * wiped from the store (the device token of a guest stays) and the reason reported; a session already open is kept.
   * Throws when the backend could not answer (NoReplyError, UNAVAILABLE) or the account is banned (BANNED): nothing is
   * wiped then.
   */
  async restoreSession(): Promise<RestoreOutcome> {
    if (this.session) return { restored: true };
    const stored = this.opts.credentials?.load() ?? {};
    if (!stored.sessionToken && !stored.refreshToken) return { restored: false, reason: 'none' };
    if (stored.refreshToken && !this.refreshToken) {
      this.refreshToken = stored.refreshToken;
      this.refreshSessionId = stored.sessionId ?? null;
      this.refreshExpiresAt = stored.refreshExpiresAt ?? null;
    }
    let refused: AuthFailureReason | null = null;
    const unauthorized = (err: unknown): AuthFailureReason | null => {
      if (err instanceof BackendError && !(err instanceof NoReplyError) && err.code === 'UNAUTHORIZED') return authFailureReason(err) ?? 'invalid_token';
      throw err;
    };
    if (stored.sessionToken) {
      try {
        await this.call('auth.resume', this.withDevice({ token: stored.sessionToken }));
        return { restored: true };
      } catch (err) {
        refused = unauthorized(err);
      }
    }
    const refreshToken = this.currentRefreshToken();
    if (refreshToken) {
      try {
        await this.call('auth.refresh', this.withDevice({ refreshToken }), { id: this.refreshRequestId(refreshToken) });
        return { restored: true };
      } catch (err) {
        refused = unauthorized(err);
      }
    }
    this.forgetStoredSession();
    return { restored: false, reason: restoreFailureOf(refused) };
  }

  /**
   * Ends the session: auth.logout on the backend (best effort: without a reply the local sign-out still happens), then
   * the session is dropped and EVERY credential stored for this origin is wiped (the device token of a guest too).
   */
  async signOut(): Promise<void> {
    try {
      if (!this.session) {
        // A stored session nobody restored yet is ended on the backend too (one attempt of each, no waiting).
        const stored = this.opts.credentials?.load() ?? {};
        if (stored.sessionToken) await this.call('auth.resume', this.withDevice({ token: stored.sessionToken }), { retries: 0 }).catch(() => undefined);
        const refreshToken = stored.refreshToken;
        if (!this.session && refreshToken) await this.call('auth.refresh', this.withDevice({ refreshToken }), { retries: 0, id: this.refreshRequestId(refreshToken) });
      }
      if (this.session) await this.call('auth.logout', {}, { retries: 0 });
    } catch {
      /* the local sign-out below happens whatever the backend answered */
    } finally {
      this.dropSession(true, true);
      this.refreshRequest = null;
      this.opts.credentials?.clear();
    }
  }

  /**
   * account.delete: deletes the signed-in player's account and all of its data for good. An account that signs in
   * with a password passes it (never kept afterwards; wrong: UNAUTHORIZED 'invalid_credentials', the session stays); a
   * guest passes nothing. Afterwards the session is dropped and EVERY credential stored for this origin is wiped (the
   * guest device token no longer opens anything).
   */
  async deleteAccount(password?: string): Promise<ResultOf<'account.delete'>> {
    const result = await this.call('account.delete', password === undefined ? {} : { password });
    this.dropSession(true, true);
    this.refreshRequest = null;
    this.opts.credentials?.clear();
    return result;
  }

  /** Drops the local session and the stored session credentials of this origin (the device token stays). */
  forgetStoredSession(): void {
    this.dropSession(true, true);
    this.refreshRequest = null;
    if (this.opts.credentials) {
      const c = this.opts.credentials.load();
      delete c.sessionToken;
      delete c.accessExpiresAt;
      delete c.refreshToken;
      delete c.refreshExpiresAt;
      delete c.sessionId;
      delete c.playerId;
      this.opts.credentials.save(c);
    }
  }

  private withDevice<P extends object>(payload: P): P & { device?: DeviceInfo } {
    return this.opts.device ? { ...payload, device: this.opts.device } : payload;
  }

  private adoptSession(r: AuthResult): void {
    this.replaced = false;
    this.trackEventsOf(r.playerId);
    // Protocol 1 backends answered with `token` / `expiresAt` only.
    const access = r.accessToken ?? r.token;
    const accessExpiresAt = r.accessExpiresAt ?? r.expiresAt;
    const sessionId = r.sessionId ?? null;
    if (r.refreshToken) {
      this.refreshToken = r.refreshToken;
      this.refreshSessionId = sessionId;
      this.refreshExpiresAt = r.refreshExpiresAt ?? null;
    } else if (this.refreshSessionId !== sessionId || sessionId === null) {
      // Bound to another session by its access token: its refresh token is the stored one, if that is its session.
      const stored = this.opts.credentials?.load() ?? {};
      const own = sessionId !== null && stored.sessionId === sessionId && stored.playerId === r.playerId;
      this.refreshToken = own ? (stored.refreshToken ?? null) : null;
      this.refreshSessionId = own ? sessionId : null;
      this.refreshExpiresAt = own ? (stored.refreshExpiresAt ?? null) : null;
    }
    this.session = { playerId: r.playerId, displayName: r.displayName, token: access, expiresAt: accessExpiresAt, username: r.username, sessionId };
    const ttl = typeof r.serverTime === 'number' ? Math.max(0, accessExpiresAt - r.serverTime) : null;
    this.accessDeadline = ttl === null ? 0 : Date.now() + ttl - Math.min(ACCESS_EXPIRY_MARGIN_MS, ttl * 0.1);
    this.transport.setAuthToken(access);
    if (this.opts.credentials) {
      const c = this.opts.credentials.load();
      c.sessionToken = access;
      c.accessExpiresAt = accessExpiresAt;
      c.playerId = r.playerId;
      if (sessionId) c.sessionId = sessionId;
      else delete c.sessionId;
      if (this.refreshToken) {
        c.refreshToken = this.refreshToken;
        if (this.refreshExpiresAt !== null) c.refreshExpiresAt = this.refreshExpiresAt;
      } else {
        delete c.refreshToken;
        delete c.refreshExpiresAt;
      }
      if (r.deviceToken) c.deviceToken = r.deviceToken;
      this.opts.credentials.save(c);
    }
    this.scheduleRefresh(ttl);
  }

  /**
   * Ends the local session. `clearStored` also forgets the stored player; `forgetRefresh` the refresh token (logout,
   * a revoked session). Stored tokens are removed only while they are this client's (another window of the same
   * backend may have stored a newer pair).
   */
  private dropSession(clearStored = true, forgetRefresh = clearStored): void {
    const access = this.session?.token ?? null;
    const refresh = this.refreshToken;
    this.session = null;
    this.accessDeadline = 0;
    this.clearRefreshTimer();
    if (forgetRefresh) {
      this.refreshToken = null;
      this.refreshSessionId = null;
      this.refreshExpiresAt = null;
    }
    this.transport.setAuthToken(null);
    if (this.opts.credentials) {
      const c = this.opts.credentials.load();
      if (access === null || c.sessionToken === access) {
        delete c.sessionToken;
        delete c.accessExpiresAt;
      }
      if (forgetRefresh && (refresh === null || c.refreshToken === refresh)) {
        delete c.refreshToken;
        delete c.refreshExpiresAt;
        delete c.sessionId;
      }
      if (clearStored) delete c.playerId;
      this.opts.credentials.save(c);
    }
  }

  private forgetRefreshToken(token: string): void {
    if (this.refreshToken === token) {
      this.refreshToken = null;
      this.refreshSessionId = null;
      this.refreshExpiresAt = null;
      this.clearRefreshTimer();
    }
    if (this.opts.credentials) {
      const c = this.opts.credentials.load();
      if (c.refreshToken === token) {
        delete c.refreshToken;
        delete c.refreshExpiresAt;
        this.opts.credentials.save(c);
      }
    }
  }

  /**
   * The refresh token to use: another window of the same backend origin that refreshed first stored a newer one (of
   * the same player), which wins over the one in memory (the old one was rotated).
   */
  private currentRefreshToken(): string | null {
    const stored = this.opts.credentials?.load();
    const playerId = this.session?.playerId ?? stored?.playerId ?? null;
    if (stored?.refreshToken && stored.playerId === playerId && stored.refreshToken !== this.refreshToken) {
      this.refreshToken = stored.refreshToken;
      this.refreshSessionId = stored.sessionId ?? null;
      this.refreshExpiresAt = stored.refreshExpiresAt ?? null;
    }
    return this.refreshToken;
  }

  /**
   * Gets a new token pair with the refresh token now (the connection is bound to it). Concurrent callers share one
   * refresh. False: there is no usable refresh token (none stored, or the backend refused it: it is forgotten).
   * Throws when the backend could not be asked (NoReplyError, UNAVAILABLE) or the account is banned.
   */
  refreshSession(): Promise<boolean> {
    if (!this.refreshing) {
      const p: Promise<boolean> = this.doRefresh().finally(() => {
        if (this.refreshing === p) this.refreshing = null;
      });
      this.refreshing = p;
    }
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    const before = this.refreshToken;
    const token = this.currentRefreshToken();
    if (!token) return false;
    // Another window rotated the pair: its stored access token binds this connection without another rotation.
    if (token !== before && (await this.resumeStored())) return true;
    try {
      await this.call('auth.refresh', this.withDevice({ refreshToken: token }), { id: this.refreshRequestId(token) });
      return true;
    } catch (err) {
      if (!(err instanceof BackendError) || err instanceof NoReplyError || err.code !== 'UNAUTHORIZED') throw err;
      // Another window refreshed at the same moment and stored the newer pair: continue with it.
      if (authFailureReason(err) === 'refresh_rotated' && this.currentRefreshToken() !== token && (await this.resumeStored())) return true;
      this.forgetRefreshToken(token);
      return false;
    }
  }

  /** The request id of every auth.refresh with `token` (see refreshRequest). */
  private refreshRequestId(token: string): string {
    if (this.refreshRequest?.token !== token) this.refreshRequest = { token, id: this.nextId() };
    return this.refreshRequest.id;
  }

  /** auth.resume with the stored access token (another window's); false when the backend refuses it. */
  private async resumeStored(): Promise<boolean> {
    const stored = this.opts.credentials?.load() ?? {};
    if (!stored.sessionToken || stored.sessionToken === this.session?.token) return false;
    try {
      await this.call('auth.resume', this.withDevice({ token: stored.sessionToken }));
      return true;
    } catch (err) {
      if (err instanceof BackendError && !(err instanceof NoReplyError) && err.code === 'UNAUTHORIZED') return false;
      throw err;
    }
  }

  /** Refreshes the access token shortly before it expires (10% of its lifetime, at most a minute, ahead). */
  private scheduleRefresh(ttlMs: number | null): void {
    this.clearRefreshTimer();
    if (this.opts.autoRefresh === false || this.closed || !this.refreshToken || ttlMs === null || ttlMs <= 0) return;
    const delay = Math.min(Math.max(1_000, ttlMs - Math.min(60_000, ttlMs * 0.1)), 24 * 3600_000);
    const timer = setTimeout(() => {
      if (this.refreshTimer !== timer) return;
      this.refreshTimer = null;
      if (this.session && !this.closed) void this.refreshSession().catch(() => undefined);
    }, delay);
    (timer as { unref?: () => void }).unref?.();
    this.refreshTimer = timer;
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
  }

  /**
   * Negotiates the protocol version and learns the server's features and limits (`serverInfo`). Throws
   * VERSION_MISMATCH when there is no common version or this client is older than the server accepts.
   */
  async hello(): Promise<ResultOf<'system.hello'>> {
    const payload: HelloPayload = {
      clientVersion: this.opts.clientVersion ?? DEFAULT_CLIENT_VERSION,
      protocols: [...SUPPORTED_PROTOCOL_VERSIONS],
      platform: this.opts.platform ?? this.opts.device?.platform ?? defaultPlatform(),
      capabilities: [...(this.opts.capabilities ?? CLIENT_CAPABILITIES)],
    };
    this.serverInfo = await this.call('system.hello', payload);
    return this.serverInfo;
  }

  /** Seq of the newest server event this client has (null = none known yet). */
  get lastSeq(): number | null {
    return this.lastEventSeq;
  }

  /** Called when events were missed and cannot be replayed (EventGap): the app reloads its state. */
  onGap(listener: (gap: EventGap) => void): () => void {
    this.gapListeners.add(listener);
    return () => this.gapListeners.delete(listener);
  }

  private emitGap(gap: EventGap): void {
    for (const l of [...this.gapListeners]) {
      try {
        l(gap);
      } catch {
        /* listener errors are isolated */
      }
    }
  }

  /**
   * Seqs are per player: a session of another player starts without a position. On a reconnecting transport the
   * client learns the current position right away (events.resume without `after`), so that a later reconnect can
   * ask for exactly what it missed.
   */
  private trackEventsOf(playerId: string): void {
    if (this.eventsPlayer === playerId) {
      // Signed in again after a reconnect whose own sign-in failed (e.g. the session was ended meanwhile): what was
      // missed is not asked for on this sign-in path; it is a gap (the app reloads its state), and the position
      // restarts with the next event.
      // (Decided after the sign-in is adopted: a reconnect's own sign-in resumes right away and clears the flag.)
      if (this.resumePending) {
        void Promise.resolve().then(() => {
          if (!this.resumePending || this.heldEvents) return;
          this.resumePending = false;
          const after = this.lastEventSeq;
          this.lastEventSeq = null;
          this.emitGap({ after, last: after ?? 0 });
        });
      }
      return;
    }
    const first = this.eventsPlayer === null;
    this.eventsPlayer = playerId;
    this.lastEventSeq = null;
    this.resumePending = false;
    if (!first) this.transport.resetEventPosition?.();
    if (this.transport.kind !== 'websocket') return;
    // After the sign-in that bound the connection has been adopted.
    void Promise.resolve()
      .then(() => this.call('events.resume', {}, { retries: 0 }))
      .then(
        (r) => {
          if (this.eventsPlayer === playerId && (this.lastEventSeq === null || r.last > this.lastEventSeq)) this.lastEventSeq = r.last;
        },
        () => undefined,
      );
  }

  /**
   * After a reconnect, before the queued requests are released: the events missed while the connection was down
   * (events.resume after the last seq), delivered in order and before the live events that arrived meanwhile. A gap
   * (events dropped from the server's buffer, a server restart, an unknown position, a failed resume) goes to the
   * onGap listeners.
   */
  private async resumeEvents(send: (request: RequestEnvelope) => Promise<ResponseEnvelope>): Promise<void> {
    this.resumePending = false;
    const after = this.lastEventSeq;
    this.heldEvents ??= [];
    let gap: EventGap | null = null;
    let delivered = after ?? -1;
    try {
      const res = await send({ v: PROTOCOL_VERSION, id: this.nextId(), type: 'events.resume', payload: after === null ? {} : { after } });
      if (!res.ok) gap = { after, last: after ?? 0 };
      else {
        const batch = res.result as EventBatch;
        if (batch.gap || after === null) {
          // The server's numbering continues after `last` (after a restart it may even be lower than `after`).
          gap = { after, last: batch.last };
          delivered = batch.last;
        } else {
          for (const ev of batch.events) {
            if (typeof ev.seq === 'number' && ev.seq <= delivered) continue;
            this.deliver(ev);
            if (typeof ev.seq === 'number') delivered = ev.seq;
          }
          delivered = Math.max(delivered, batch.last);
        }
      }
    } catch {
      gap = { after, last: after ?? 0 };
    } finally {
      const held = this.heldEvents ?? [];
      this.heldEvents = null;
      // Live events received on the new connection are new to this client; the resumed ones come first.
      for (const ev of held.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))) {
        if (gap === null && typeof ev.seq === 'number' && ev.seq <= delivered) continue;
        this.deliver(ev);
        if (typeof ev.seq === 'number') delivered = Math.max(delivered, ev.seq);
      }
      if (delivered >= 0) this.lastEventSeq = gap ? Math.max(gap.last, delivered) : delivered;
      if (gap) this.emitGap({ after: gap.after, last: this.lastEventSeq ?? gap.last });
    }
  }

  /**
   * Restores or creates a session: the stored access token -> `auth.resume` (a protocol 1 session token is converted
   * by the backend); otherwise (or when it expired) the stored refresh token -> `auth.refresh`; only when that is gone
   * or refused and `opts.guest` allows it (default true), the stored device token -> `auth.guest`, otherwise a new
   * guest account. Without the guest fallback: UNAUTHORIZED 'session_required' (the app shows its sign-in screen).
   * After the session was replaced by a sign-in elsewhere (`sessionReplaced`): UNAUTHORIZED 'session_replaced', nothing
   * is sent, unless `opts.takeOver` (the player asked to sign in here again: that ends the other session).
   */
  async ensureSession(displayName?: string, opts: { guest?: boolean; takeOver?: boolean } = {}): Promise<ClientSession> {
    if (this.session) return this.session;
    if (this.replaced && !opts.takeOver) throw new BackendError('UNAUTHORIZED', 'This account signed in on another device', { reason: 'session_replaced' });
    const stored = this.opts.credentials?.load() ?? {};
    if (stored.refreshToken && !this.refreshToken) {
      this.refreshToken = stored.refreshToken;
      this.refreshSessionId = stored.sessionId ?? null;
      this.refreshExpiresAt = stored.refreshExpiresAt ?? null;
    }
    if (stored.sessionToken) {
      try {
        await this.call('auth.resume', this.withDevice({ token: stored.sessionToken }));
        return this.session!;
      } catch (err) {
        if (!(err instanceof BackendError) || err.code === 'UNAVAILABLE' || err.code === 'BANNED') throw err;
      }
    }
    if (this.currentRefreshToken() && (await this.refreshSession())) return this.session!;
    if (opts.guest === false) throw new BackendError('UNAUTHORIZED', 'Sign in required', { reason: 'session_required' });
    const payload: PayloadOf<'auth.guest'> = this.withDevice({});
    if (stored.deviceToken) payload.deviceToken = stored.deviceToken;
    if (displayName) payload.displayName = displayName;
    await this.call('auth.guest', payload);
    return this.session!;
  }

  on<E extends EventType>(event: E, handler: (payload: EventMap[E]) => void): () => void {
    let set = this.handlers.get(event);
    if (!set) this.handlers.set(event, (set = new Set()));
    set.add(handler as (p: unknown) => void);
    return () => set!.delete(handler as (p: unknown) => void);
  }

  onAny(handler: (ev: EventEnvelope) => void): () => void {
    this.anyHandlers.add(handler);
    return () => this.anyHandlers.delete(handler);
  }

  /** Delivers the held events in seq order and stops holding. */
  private releaseHeld(): void {
    const held = this.heldEvents;
    this.heldEvents = null;
    if (held) for (const ev of held.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0))) this.deliver(ev);
  }

  private dispatch(ev: EventEnvelope): void {
    if (this.heldEvents) {
      this.heldEvents.push(ev);
      return;
    }
    this.deliver(ev);
  }

  private deliver(ev: EventEnvelope): void {
    if (typeof ev.seq === 'number') this.lastEventSeq = this.lastEventSeq === null ? ev.seq : Math.max(this.lastEventSeq, ev.seq);
    for (const h of [...(this.handlers.get(ev.event) ?? [])]) {
      try {
        h(ev.payload);
      } catch {
        /* handler errors are isolated */
      }
    }
    for (const h of [...this.anyHandlers]) {
      try {
        h(ev);
      } catch {
        /* ignore */
      }
    }
  }

  /**
   * The app returned to the foreground after its timers and sockets may have been frozen (a phone's pause/resume, a
   * sleeping laptop): a lost connection is re-established at once (Transport.wake) instead of after its backoff wait; a
   * connection that looks open is probed (system.time) and replaced when it does not answer within `probeTimeoutMs`
   * (the reconnect handler then binds the new one: auth.resume, or auth.refresh when the access token ran out); and an
   * access token that expired while the refresh timer could not run is refreshed now. Never signs in or out by itself;
   * after the session was replaced by a sign-in elsewhere it does nothing (no connection is opened).
   */
  async wake(opts: { probeTimeoutMs?: number } = {}): Promise<void> {
    if (this.closed || this.replaced) return;
    this.transport.wake?.();
    if (this.transport.restart) {
      const probe: RequestEnvelope = { v: PROTOCOL_VERSION, id: this.nextId(), type: 'system.time', payload: {} };
      try {
        await this.transport.send(probe, { timeoutMs: opts.probeTimeoutMs ?? WAKE_PROBE_TIMEOUT_MS });
      } catch (err) {
        if (this.closed) return;
        if (err instanceof TransportError && err.retryable) this.transport.restart();
      }
    }
    if (!this.closed && this.session && this.accessDeadline !== 0 && Date.now() >= this.accessDeadline) await this.refreshSession().catch(() => false);
  }

  async close(): Promise<void> {
    this.closed = true;
    this.clearRefreshTimer();
    for (const off of this.offTransport) off();
    this.transport.setReconnectHandler?.(null);
    await this.transport.close();
  }
}
